import Helmet from "react-helmet";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Alert from "../../../../components/Alert";
import Grid from "../../../../components/Grid";
import Members from "../../components/MembersList";
import { Page, PageHeader, Title } from "../../components/styles";
import { connect, useDispatch } from "react-redux";
import { getGroupById, getGroupSubscribers, getMetrics } from "../../../../store/actions/group";
import Button from '../../../../components/Button'
import MembersListFilters, { FilterConfig, FilterGroup } from "../../../../components/MembersListFilters";
import Tabs from "./../components/Tabs";
import InviteMembers from "../../components/MembersList/components/InviteMembers";
import { Group, PlanDto } from "../../../../types";
import { useLayout } from "../../../../providers/LayoutProvider";
import FloatingMenu from "./components/FloatingMenu";
import { useWindowSize } from "../../../../hooks/useWindowSize";
import ModalShareLink from "../../components/ModalShareLink";
import DoorStatusBadge from "../../components/DoorStatusBadge";

interface Filter {
  [field: string]: (string | number)[];
}

interface ComponentProps {
  match,
  group: Group;
  groupStates: {
    loading: boolean,
    error: string | boolean,
    success: boolean
  }
  groupSubscribersInformation;
  groupSubscribersStates;
}

const periodsUnitsTranslations = {
  "month": { singular: "mes", plural: "meses" },
  "quarter": { singular: "trimestre", plural: "trimestres" },
  "half-year": { singular: "semestre", plural: "semestres" },
  "year": { singular: "año", plural: "años" },
}

const Component = ({
  match,
  group,
  groupStates,
  groupSubscribersInformation,
  groupSubscribersStates,
}: ComponentProps) => {
  const { isMobile } = useWindowSize();
  const dispatch = useDispatch();
  const { layout, setLayout } = useLayout();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<{ value: string, label: string }[]>([])
  const [isOpened, setIsOpened] = useState(false);

  useEffect(() => {
    if (
      !!match.params &&
      (!group || (!!group && group?.id !== Number(match?.params?.idGroup))) &&
      !groupStates.loading
    ) {
      dispatch(getGroupById({ group_id: match.params.idGroup }));
      dispatch(getMetrics({ id: match.params.idGroup, filters: [] }));
    }
  }, [match]);

  const getSubscribres = () => {
    dispatch(getGroupSubscribers({ page, id: group?.id, filters }));
  };

  const paginate = (page) => {
    setPage(page);
  };

  const uniqueCurrencies = useMemo(() => {
    return (group?.plans as PlanDto[])?.reduce((acc: { value: string, label: string }[], plan: PlanDto) => {
      if (!acc.some(item => item?.value === plan?.currency) && !!plan?.currency) {
        acc.push({ value: plan.currency, label: plan.currency });
      }
      return acc;
    }, []);
  }, [group]);

  const getFriendlyPeriod = (plan: PlanDto) => {
    if (plan.type === 'subscription') {
      const isPlural = plan?.period && plan?.period > 1;
      const period = plan?.period_unit && periodsUnitsTranslations[plan?.period_unit]
      return `Cada ${plan?.period} ${period[isPlural ? "plural" : "singular"]}`
    } else {
      return "Pago único"
    }
  }

  const generatePeriodList = useMemo((): {
    value: string; // Concatenación de plan_ids
    label: string; // Texto descriptivo del período
  }[] => {
    const plans = group?.plans;
    const currencies = selectedCurrency.map(item => item.value)
    // Filtrar planes activos y, si se proporciona, filtrar por moneda
    const filteredPlans = plans?.filter(
      plan => plan?.status === "active" && (currencies.length <= 0 || currencies.includes(plan?.currency))
    );

    // Agrupar planes por período y unidad
    const groupedByPeriod = filteredPlans?.reduce<Record<string, { value: string[]; label: string }>>(
      (acc, plan) => {
        const periodKey = plan?.period && plan?.period_unit
          ? `${plan?.period}-${plan?.period_unit}`
          : "one-time"; // Clave para pago único

        if (!acc[periodKey]) {
          acc[periodKey] = {
            value: [],
            label: plan?.period && plan?.period_unit
              ? getFriendlyPeriod(plan)
              : "Pago único",
          };
        }

        acc[periodKey].value.push(plan?.id.toString());
        return acc;
      },
      {}
    );

    if (groupedByPeriod) {
      // Convertir el resultado en un array de objetos
      return Object.values(groupedByPeriod)?.map(group => ({
        value: group?.value.join(", "),
        label: group?.label,
      }));
    }

    return []
  }, [group, selectedCurrency]);

  // Función para manejar el submit de los filtros
  const handleFiltersSubmit = (values: any) => {
    const { status, ...rest } = values;

    const apiStatuses = status?.map((option) => {
      if (option === "payment_issue") {
        return ["renewal_retrying", "retrying", "not_paid", "void"];
      }

      if (option === "all_cancelled") {
        return ["cancelled", "expired"]
      }

      return option;
    });

    setFilters({ ...rest, ...(status && status.length > 0 ? { status: apiStatuses } : {}) });
  };

  // Función para limpiar filtros
  const handleClearFilters = () => {
    setFilters([]);
    setSelectedCurrency([]);
  };

  // Configuración de filtros para MembersListFilters
  const filtersGroup: FilterGroup[] = useMemo(() => [
    {
      title: "Buscar",
      filters: [
        {
          name: 'email',
          label: 'Búsqueda general',
          type: 'input',
          placeholder: 'Email, nombre o apellido'
        },
        {
          name: 'status',
          label: 'Estado',
          type: 'multi',
          options: [
            { value: 'active', label: 'Activo' },
            { value: 'all_cancelled', label: 'Cancelado' },
            { value: 'non_renewing', label: 'Próximo a vencer' },
            { value: 'pending_invite', label: 'Invitado Pendiente' },
            { value: 'payment_issue', label: 'Problemas con el pago' },
          ],
          placeholder: 'Seleccionar estado'
        },
        {
          name: 'currency',
          label: 'Moneda',
          type: 'multi',
          options: uniqueCurrencies,
          placeholder: 'Seleccionar moneda'
        },
        {
          name: 'plan',
          label: 'Periodo de pago',
          type: 'multi',
          options: generatePeriodList,
          placeholder: 'Seleccionar periodo'
        },
      ]
    },
    {
      title: "Ordenamiento",
      filters: [
        {
          name: 'sort_by',
          label: 'Ordenar por',
          type: 'select',
          options: [
            { value: 'name:asc', label: 'Nombre: A a Z' },
            { value: 'name:desc', label: 'Nombre: Z a A' },
            { value: 'email:asc', label: 'Email: A a Z' },
            { value: 'email:desc', label: 'Email: Z a A' },
            { value: 'started_at:desc', label: 'Miembros nuevos primero' },
            { value: 'started_at:asc', label: 'Miembros antiguos primero' }
          ],
          placeholder: 'Seleccionar orden'
        }
      ]
    },

  ], [uniqueCurrencies, generatePeriodList]);

  useEffect(() => {
    getSubscribres();
  }, [filters, page, group]);

  useEffect(() => {
    setLayout({
      ...layout,
      page: {
        title: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {group?.group_name}
          </div>
        ),
        subtitle: <DoorStatusBadge group={group} />,
        description: `Listado de miembros de tu membresía ${group?.group_name}.`,
        //back: `/dashboard/${group?.group_type}/${group?.id}`,
        back: undefined,
      },
      dashboard: {
        tabs: {
          visible: true,
          activeTab: "members",
          component: [<Tabs active="members" />],
        },
      },
      header: {
        visible: true,
        actions: !isMobile ? [<Button
          onClick={() => {
            setIsOpened(true)
          }}
          options={{
            block: true,
            type: "filled",
            size: "lg",
            skin: "purple",
          }}
        >
          Agregar Miembros
        </Button>] : []
      },
      show_whatsapp: !isMobile,
    });
  }, [group, isOpened, isMobile]);

  return (
    <Grid.ContainerFluid>
      <Grid.Row>
        <Grid.Col>
          <Alert
            visible={true}
            title="Aumenta el numero de suscriptores!"
            text="No olvides promocionar tu grupo compartiendo el Link en tus redes sociales."
            id="aumenta-tus-suscriptores"
          />

          <div style={{ height: 30 }} />

          <MembersListFilters
            title="Filtros"
            showClearButton={true}
            filtersGroup={filtersGroup}
            onSubmit={handleFiltersSubmit}
            onClear={handleClearFilters}
            loading={groupSubscribersStates.loading}
            appliedFilters={filters}
          />

          <Members
            states={groupSubscribersStates}
            data={groupSubscribersInformation?.data}
            filters={filters}
            pagination={groupSubscribersInformation?.pagination}
            goToPage={paginate}
            groupId={group?.id}
          />

          <FloatingMenu setIsOpened={setIsOpened} />

          <ModalShareLink />

          <InviteMembers isOpened={isOpened} onClose={() => setIsOpened(false)} />
        </Grid.Col>
      </Grid.Row>
    </Grid.ContainerFluid>
  );
};

const state = ({ groupStore }) => {
  const { data: groupSubscribersInformation, states: groupSubscribersStates } =
    groupStore.groupSubscribers;
  const { data: group, states: groupStates } = groupStore.group;

  return {
    group,
    groupStates,
    groupSubscribersInformation,
    groupSubscribersStates,
  };
};

export default connect(state)(Component);